import type { TActivityFe } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/Activity.type";
import type { TActivity2Fe } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/Activity2.type";
import type { TActivityValue2ObjectFilterValue } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/ActivityValue2.type";
import type { EActivityStatus } from "@timeedit/activity-manager-shared-lib/internal/types/Activity/ActivityStatus.enum";
import Language from "../../lib/Language";

export type TExtIdKind = "object" | "field" | "type";
export type TActivityLinePart = { text: string } | { extId: string; kind: TExtIdKind };

export type TObjectFilter = {
    typeExtId: string;
    categories: { fieldExtId: string; values: string[] }[];
    searchString?: string;
};

export type TActivityRow = {
    key: string;
    label: TActivityLinePart;
    lines: TActivityLinePart[][];
    filters: TObjectFilter[];
    dateRange?: {
        startTime?: string | number;
        endTime?: string | number;
        timeZone?: string;
    };
};

export const isActivity2 = (activity: TActivityFe | TActivity2Fe): activity is TActivity2Fe =>
    activity.dataVersion === 2;

type TLanguageKey = Parameters<typeof Language.get>[0];

const activityStatusKeys = {
    NOT_SCHEDULED: "nc_activity_status_not_scheduled",
    SCHEDULED: "nc_activity_status_scheduled",
    VALIDATION_ERROR: "nc_activity_status_validation_error",
    FAILED: "nc_activity_status_failed",
    QUEUED: "nc_activity_status_queued",
    SCHEDULING_IN_PROGRESS: "nc_activity_status_in_progress",
    GROUPED_MULTIPLE: "nc_activity_status_grouped_multiple",
} as const satisfies Partial<Record<EActivityStatus, TLanguageKey>>;

type TActivityStatusKey = (typeof activityStatusKeys)[keyof typeof activityStatusKeys];

export const getActivityStatusKey = (status: EActivityStatus): TActivityStatusKey | undefined =>
    (activityStatusKeys as Partial<Record<EActivityStatus, TActivityStatusKey>>)[status];

export const toStringArray = (value: unknown): string[] => {
    if (value === null || value === undefined || value === "") {
        return [];
    }
    if (Array.isArray(value)) {
        return value.flatMap(toStringArray);
    }
    if (typeof value === "object") {
        return [JSON.stringify(value)];
    }
    return [String(value)];
};

export const toTextLines = (value: unknown): TActivityLinePart[][] =>
    toStringArray(value).map((text) => [{ text }]);

export const toExtIdLines = (extIds: string[], kind: TExtIdKind): TActivityLinePart[][] =>
    extIds.map((extId) => [{ extId, kind }]);

export const isObjectFilterValue = (value: unknown): value is TActivityValue2ObjectFilterValue =>
    typeof value === "object" && value !== null && "categories" in value;

export const toObjectFilter = (
    typeExtId: string,
    { categories, searchString }: TActivityValue2ObjectFilterValue
): TObjectFilter => ({
    typeExtId,
    categories: categories.map((category) => ({
        fieldExtId: category.id,
        values: category.values,
    })),
    searchString: searchString || undefined,
});

export const collectExtIds = (rows: TActivityRow[]): Record<TExtIdKind, string[]> => {
    const extIds: Record<TExtIdKind, Set<string>> = {
        object: new Set(),
        field: new Set(),
        type: new Set(),
    };
    rows.forEach((row) => {
        [row.label, ...row.lines.flat()].forEach((part) => {
            if ("extId" in part) {
                extIds[part.kind].add(part.extId);
            }
        });
        row.filters
            .flatMap((filter) => filter.categories)
            .forEach((category) => extIds.field.add(category.fieldExtId));
    });
    return {
        object: [...extIds.object],
        field: [...extIds.field],
        type: [...extIds.type],
    };
};
